import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { previewToDOM } from '@office-kit/docx-preview';
import { captureException } from '@sentry/react';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

import {
    AttachmentDestination,
    CloudAttachment,
    CloudStorageConnection,
    getCloudAttachmentContent,
    getDocumentOntologyCatalog,
} from '@/services';

import type { AttachmentDocumentReview } from '../AttachmentPreview';
import { DOCX_MIME_TYPE } from '../AttachmentPreview';
import type { DocumentConceptReference, DocumentIntelligence } from '../AttachmentRedactionEditor/documentIntelligence';
import {
    buildDocumentIntelligence,
    EMPTY_DOCUMENT_INTELLIGENCE,
} from '../AttachmentRedactionEditor/documentIntelligence';
import type { ExtractedDocument, ExtractedDocumentPage } from '../documentExtraction';
import { buildPdfExtractedDocument, buildTextExtractedDocument, extractPdfTextPages } from '../documentExtraction';
import { buildRenderedDocxIntelligence } from '../DocxAttachmentPreview/docxLayout';

GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

interface UseOntologyDocumentInput {
    attachment: CloudAttachment;
    connection: CloudStorageConnection;
    destination?: AttachmentDestination;
    docxContainerRef: RefObject<HTMLDivElement>;
    onReviewChange?: (review?: AttachmentDocumentReview) => void;
}

interface OntologyDocumentState {
    document?: ExtractedDocument;
    error?: string;
    intelligence: DocumentIntelligence;
    loading: boolean;
}

const waitForLayout = () => new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));

const textPageFromDocument = (document: ExtractedDocument): ExtractedDocumentPage => ({
    items: document.blocks.map((block, index) => {
        const start = document.blocks
            .slice(0, index)
            .reduce((total, candidate) => total + candidate.text.length + 2, 0);

        return {
            direction: 'ltr',
            end: start + block.text.length,
            hasEol: true,
            height: 0.01,
            index,
            page: 1,
            start,
            text: block.text,
            width: 1,
            x: 0,
            y: Math.min(index * 0.02, 0.98),
        };
    }),
    page: 1,
    text: document.plainText,
});

const getTextContent = async (blob: Blob, mimeType: string) => {
    const rawText = await blob.text();

    if (mimeType !== 'application/json') {
        return rawText;
    }

    try {
        return JSON.stringify(JSON.parse(rawText), null, 2);
    } catch {
        return rawText;
    }
};

const useOntologyDocument = ({
    attachment,
    connection,
    destination,
    docxContainerRef,
    onReviewChange,
}: UseOntologyDocumentInput) => {
    const callbacksRef = useRef({ onReviewChange });
    const documentInputRef = useRef({ attachment, connection, destination });
    const [state, setState] = useState<OntologyDocumentState>({
        intelligence: EMPTY_DOCUMENT_INTELLIGENCE,
        loading: true,
    });
    const [activeReferenceId, setActiveReferenceId] = useState<string>();

    useEffect(() => {
        callbacksRef.current = { onReviewChange };
        documentInputRef.current = { attachment, connection, destination };
    }, [attachment, connection, destination, onReviewChange]);

    const selectReference = useCallback(
        (reference: DocumentConceptReference) => {
            setActiveReferenceId(reference.id);
            const root = document.querySelector<HTMLElement>(`#ontology-document\\:${attachment.id}`);
            const tag = Array.from(root?.querySelectorAll<HTMLElement>('[data-ontology-reference-id]') ?? []).find(
                (candidate) => candidate.dataset.ontologyReferenceId === reference.id,
            );

            if (tag) {
                tag.scrollIntoView({ behavior: 'smooth', block: 'center' });
                tag.focus({ preventScroll: true });
                return;
            }

            const candidates = Array.from(root?.querySelectorAll<HTMLElement>('[data-ontology-block-id]') ?? []);
            const normalizedReference = reference.text.replace(/\s+/g, ' ').trim().toLocaleLowerCase();
            const block = candidates.find(
                (candidate) =>
                    Number(candidate.dataset.ontologyPage) === reference.page &&
                    candidate.textContent?.replace(/\s+/g, ' ').toLocaleLowerCase().includes(normalizedReference),
            );

            block?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        },
        [attachment.id],
    );

    useEffect(() => {
        if (state.loading || !state.document) {
            return;
        }

        callbacksRef.current.onReviewChange?.({
            activeReferenceId,
            document: state.document,
            intelligence: state.intelligence,
            loading: false,
            onReferenceSelect: selectReference,
            textExtractionSupported: state.document.status !== 'ocr-required',
        });
    }, [activeReferenceId, selectReference, state]);

    useEffect(() => {
        const controller = new AbortController();
        const documentInput = documentInputRef.current;
        let activePdf: PDFDocumentProxy | undefined;
        let disposeDocx: (() => void) | undefined;

        callbacksRef.current.onReviewChange?.(undefined);

        void getCloudAttachmentContent({ ...documentInput, signal: controller.signal })
            .then(async (blob) => {
                const ontology = await getDocumentOntologyCatalog();

                if (controller.signal.aborted) {
                    return undefined;
                }

                if (documentInput.attachment.mimeType === 'application/pdf') {
                    activePdf = await getDocument({ data: await blob.arrayBuffer() }).promise;
                    const pages = await extractPdfTextPages(activePdf, controller.signal);

                    return {
                        document: buildPdfExtractedDocument(pages, {
                            mimeType: documentInput.attachment.mimeType,
                            name: documentInput.attachment.name,
                        }),
                        intelligence: buildDocumentIntelligence(pages, ontology),
                    };
                }

                if (documentInput.attachment.mimeType === DOCX_MIME_TYPE) {
                    const container = docxContainerRef.current;

                    if (!container) {
                        throw new Error('The semantic DOCX extraction surface is unavailable.');
                    }

                    const handle = await previewToDOM(blob, container, {
                        breakPages: true,
                        classPrefix: 'attachment-docx-',
                        inWrapper: true,
                        renderFonts: true,
                    });

                    disposeDocx = handle.dispose;
                    await document.fonts?.ready;
                    await waitForLayout();
                    return buildRenderedDocxIntelligence(container, ontology, {
                        mimeType: documentInput.attachment.mimeType,
                        name: documentInput.attachment.name,
                    });
                }

                const extractedDocument = buildTextExtractedDocument(
                    await getTextContent(blob, documentInput.attachment.mimeType),
                    {
                        mimeType: documentInput.attachment.mimeType,
                        name: documentInput.attachment.name,
                    },
                );

                return {
                    document: extractedDocument,
                    intelligence: buildDocumentIntelligence([textPageFromDocument(extractedDocument)], ontology),
                };
            })
            .then((result) => {
                if (controller.signal.aborted || !result) {
                    return;
                }

                const nextState = {
                    document: result.document,
                    intelligence: result.intelligence,
                    loading: false,
                };

                setActiveReferenceId(undefined);
                setState(nextState);
            })
            .catch((error: unknown) => {
                if (controller.signal.aborted || (error instanceof DOMException && error.name === 'AbortError')) {
                    return;
                }

                captureException(error);
                setState({
                    error: 'This attachment could not be normalized into a semantic document.',
                    intelligence: EMPTY_DOCUMENT_INTELLIGENCE,
                    loading: false,
                });
            });

        return () => {
            controller.abort();
            disposeDocx?.();
            void activePdf?.destroy();
            callbacksRef.current.onReviewChange?.(undefined);
        };
    }, [
        attachment.id,
        attachment.mimeType,
        connection.id,
        destination?.listName,
        destination?.recordName,
        destination?.resourceId,
        destination?.resourceName,
        destination?.resourceType,
        docxContainerRef,
        selectReference,
    ]);

    return { ...state, activeReferenceId, selectReference };
};

export default useOntologyDocument;
